import { RootState } from '@/store'
import { AsyncData, DataStatus } from '@repo/ui/utils'
import { ReactNode } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { createSelector } from 'reselect'

const debuggerMode = process.env.NODE_ENV !== 'production' && false

const logger = (
  selectors: Record<any, any>,
  key: keyof typeof DataStatus,
  options?: { data?: any; match?: string },
) => {
  if (!debuggerMode) return
  const s = Object.values(selectors).toString()
  if (s.includes(options?.match || '')) {
    console.warn('Debugger AsyncData:', {
      selectors: s,
      [key]: true,
      data: options?.data,
    })
  }
}

type UseAsyncDataResult<T extends any[]> =
  | {
      isFulfilled: true
      isLoading: false
      isError: false
      isUninitialized: false
      data: T
    }
  | {
      isFulfilled: false
      isLoading: boolean
      isError: boolean
      isUninitialized: boolean
      data: { [K in keyof T]: T[K] | undefined }
    }
export function useAsyncData<T extends any[]>(selectors: {
  [K in keyof T]: (state: RootState) => AsyncData<T[K]>
}): UseAsyncDataResult<T> {
  const createCombinedSelector = createSelector(
    selectors.map((fn) => fn),
    (...asyncDatas): UseAsyncDataResult<T> => {
      const data = asyncDatas.map((asyncData) =>
        DataStatus.isFulfilled(asyncData) ? asyncData : undefined,
      ) as { [K in keyof T]: T[K] | undefined }

      const isUninitialized = asyncDatas.every((d) =>
        DataStatus.isUninitialized(d),
      )
      if (isUninitialized) {
        if (debuggerMode) logger(selectors, 'isUninitialized')
        return {
          data,
          isError: false,
          isFulfilled: false,
          isLoading: false,
          isUninitialized: true,
        }
      }

      const isLoading = asyncDatas.some((d) => DataStatus.isLoading(d))
      if (isLoading) {
        if (debuggerMode) logger(selectors, 'isLoading')
        return {
          data,
          isError: false,
          isFulfilled: false,
          isLoading: true,
          isUninitialized: false,
        }
      }

      const isFulfilled = asyncDatas.every((d) => DataStatus.isFulfilled(d))
      if (isFulfilled) {
        if (debuggerMode) logger(selectors, 'isFulfilled', { data })
        return {
          // Cast to T since we know it's fulfilled
          data: data as T,
          isError: false,
          isFulfilled: true,
          isLoading: false,
          isUninitialized: false,
        }
      }

      if (debuggerMode) logger(selectors, 'isError')
      return {
        data,
        isError: true,
        isFulfilled: false,
        isLoading: false,
        isUninitialized: false,
      }
    },
  )

  // Get the result with shallowEqual comparison
  return useSelector(createCombinedSelector, shallowEqual)
}

type DataProviderProps<T extends any[]> = {
  errorComponent?: ReactNode
  loadingComponent?: ReactNode
  placeholderComponent?: ReactNode
  render: (...args: T) => ReactNode
  selectors: { [K in keyof T]: (state: RootState) => AsyncData<T[K]> }
}

export function DataProvider<T extends any[]>({
  errorComponent,
  loadingComponent,
  placeholderComponent,
  render,
  selectors,
}: DataProviderProps<T>) {
  // Get the data from the store
  const { data, isError, isFulfilled, isLoading, isUninitialized } =
    useAsyncData<T>(selectors)

  // Checks order is important
  if (isFulfilled) return <>{render(...data)}</>
  else if (isLoading) return <>{loadingComponent}</>
  else if (isError) return <>{errorComponent}</>
  else if (isUninitialized && placeholderComponent)
    return <>{placeholderComponent}</>
  else return null
}
